/**
 * Shared date formatting so every page shows dates the same way.
 * Absolute values use the browser locale ("Oct 19, 2026, 12:00 PM");
 * relative values read "5 min ago" / "in 2 d" and fall back to the date after a week.
 */

export type DateInput = string | number | Date | null | undefined;

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;
const NAIVE_TIMESTAMP = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/;

function parseString(value: string): Date {
  // DATE columns ("2026-10-20") are calendar days: read them as local midnight, not UTC.
  const day = DATE_ONLY.exec(value);
  if (day) return new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3]));
  // TIMESTAMP columns come without an offset and the database runs in UTC.
  if (NAIVE_TIMESTAMP.test(value)) return new Date(`${value.replace(' ', 'T')}Z`);
  return new Date(value);
}

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const date = value instanceof Date ? value : typeof value === 'string' ? parseString(value) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Pass `timeZone` to show a fixed zone with its abbreviation ("Oct 19, 2026, 12:00 PM CDT"),
 * e.g. America/Chicago where the page sits next to server-stamped Chicago times.
 */
export function formatDateTime(value?: DateInput, fallback = '-', timeZone?: string): string {
  const date = toDate(value);
  if (!date) return fallback;
  if (!timeZone) return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  // en-US keeps the zone abbreviation stable (CDT/CST) whatever the browser locale.
  return date.toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    timeZone, timeZoneName: 'short',
  });
}

export function formatDate(value?: DateInput, fallback = '-'): string {
  const date = toDate(value);
  return date ? date.toLocaleDateString(undefined, { dateStyle: 'medium' }) : fallback;
}

/**
 * Whole-day index of the local calendar day, parsed with the same rules as formatDate
 * (DATE strings are local days, naive timestamps are UTC). Differences give day counts, DST-safe.
 */
export function localDayIndex(value?: DateInput): number | null {
  const date = toDate(value);
  if (!date) return null;
  return Math.round(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / DAY);
}

export function formatRelative(value?: DateInput, now: Date = new Date(), fallback = '-'): string {
  const date = toDate(value);
  if (!date) return fallback;
  const diff = now.getTime() - date.getTime();
  const abs = Math.abs(diff);
  if (abs < MINUTE) return 'just now';
  if (abs >= WEEK) return formatDate(date, fallback);
  let amount: string;
  if (abs < HOUR) amount = `${Math.floor(abs / MINUTE)} min`;
  else if (abs < DAY) amount = `${Math.floor(abs / HOUR)} h`;
  else amount = `${Math.floor(abs / DAY)} d`;
  return diff >= 0 ? `${amount} ago` : `in ${amount}`;
}

/** ISO string for a <time dateTime>, or undefined when the value is not a date. */
export function toIsoString(value?: DateInput): string | undefined {
  const date = toDate(value);
  return date ? date.toISOString() : undefined;
}

/** The browser's IANA time zone, e.g. "America/New_York" (undefined if the runtime can't tell). */
export function viewerTimeZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}
